import React from 'react';
import { Button } from './ui/button';
import { CheckCircle2 } from 'lucide-react';
import { Appointment } from '../api/appointment';
import { getUserTimezone } from '../utils/date';
import confetti from 'canvas-confetti';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment;
}

const ConfirmationModal = ({ isOpen, onClose, appointment }: ConfirmationModalProps) => {
  React.useEffect(() => {
    if (isOpen) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tz = getUserTimezone();

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: tz
    });
  };

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz
    });
  };

  // For Google Calendar
  const appointmentDate = new Date(appointment.date);
  const appointmentEndDate = new Date(appointmentDate.getTime() + 60 * 60 * 1000); // 1 hour later

  const formatGoogleCalendarDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 animate-in fade-in duration-300">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
          </div>
          
          <h2 className="text-2xl font-bold mb-2" style={{color: '#00c4cc'}}>
            Rendez-vous confirmé !
          </h2>
          <p className="text-gray-600 mb-6">
            Votre consultation a été réservée avec succès.
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className="text-gray-700">
                  {formatDate(appointment.date)}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span className="text-gray-700">
                  {formatTime(appointment.date)}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span className="text-gray-700">
                  {appointment.therapist?.name || appointment.therapist?.firstName || 'Thérapeute'}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={onClose}
              className="w-full text-white"
              style={{backgroundColor: '#00c4cc'}}
            >
              Fermer
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                const therapistName = appointment.therapist?.name || appointment.therapist?.firstName || 'Thérapeute';
                const calendarUrl = 'https://calendar.google.com/calendar/render?' + 
                  'action=TEMPLATE' +
                  '&text=' + encodeURIComponent(`Consultation Yapadra avec ${therapistName}`) +
                  '&details=' + encodeURIComponent('Consultation vocale sur Yapadra') +
                  '&dates=' + formatGoogleCalendarDate(appointmentDate) +
                  '/' + formatGoogleCalendarDate(appointmentEndDate);
                
                window.open(calendarUrl, '_blank');
              }}
            >
              Ajouter à Google Calendar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ConfirmationModal };